import { useEffect, useState, useRef } from "react";
import {
    Box,
    Stack,
    Typography,
    Textarea as JoyTextarea,
    IconButton as JoyIconButton,
    Avatar,
    Tooltip,
    Sheet,
} from "@mui/joy";
import { Send, Pencil, Trash2, X, Check, Clock, MessageSquare, StickyNote, ChevronRight } from "lucide-react";
import moment from "moment";

import { useNotes } from "../../hooks/query";
import { useAuth } from "../../hooks";
import { Button, IconButton } from "../../shared";
import { INote } from "../../interfaces";

interface NotesProps {
    entityType: string;
    entityId: string;
    height?: string | number;
    density?: "normal" | "compact";
    hideHeader?: boolean;
}

export default function NotesWidget({
    entityType,
    entityId,
    height = "100%",
    density = "normal",
    hideHeader = false,
}: NotesProps) {
    const [newComment, setNewComment] = useState("");
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState("");
    const [notesList, setNotesList] = useState<INote[]>([]);
    const [loading, setLoading] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);

    const { useList, useCreate, useUpdate, useDelete } = useNotes();
    const listSvc = useList();
    const createSvc = useCreate();
    const updateSvc = useUpdate();
    const deleteSvc = useDelete();

    const { user } = useAuth();

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const result = await listSvc.mutateAsync({ EntityType: entityType, EntityId: entityId });
            setNotesList(result);
        } catch (e) {
            console.error("Failed to fetch notes:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entityId && entityType) {
            fetchNotes();
        }
    }, [entityId, entityType]);

    useEffect(() => {
        if (scrollRef.current && notesList.length > 0) {
            scrollRef.current.scrollTo({ top: 0, behavior: "auto" });
        }
    }, [notesList.length, density]); // Also scroll on density change

    const handleSubmit = async () => {
        if (!newComment.trim()) return;
        try {
            await createSvc.mutateAsync({
                EntityType: entityType,
                EntityId: entityId,
                Content: newComment,
            });
            setNewComment("");
            fetchNotes();
        } catch (e) {
            console.error("Failed to add note:", e);
        }
    };

    const handleEditStart = (note: INote) => {
        setEditingNoteId(note.Id);
        setEditContent(note.Content);
    };

    const handleEditCancel = () => {
        setEditingNoteId(null);
        setEditContent("");
    };

    const handleEditSave = async (id: string) => {
        if (!editContent.trim()) return;
        try {
            await updateSvc.mutateAsync({ Id: id, Content: editContent });
            setEditingNoteId(null);
            fetchNotes();
        } catch (e) {
            console.error("Failed to update note:", e);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            try {
                await deleteSvc.mutateAsync(id);
                fetchNotes();
            } catch (e) {
                console.error("Failed to delete note:", e);
            }
        }
    };

    const getInitials = (name?: string) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    const isCompact = density === "compact";

    return (
        <Box
            sx={{
                height: height,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.surface",
                //border: "1px solid",
                //borderColor: "divider",
                borderRadius: "md",
                overflow: "hidden",
                boxShadow: "sm",
            }}
        >
            {/* Minimal Header */}
            {!hideHeader && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.level1",
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            sx={{
                                p: 0.5,
                                // borderRadius: "6px",
                                bgcolor: "primary.softBg",
                                color: "primary.main",
                                display: "flex",
                            }}
                        >
                            <StickyNote size={16} />
                        </Box>
                        <Typography level="title-sm" sx={{ fontWeight: 700, letterSpacing: "0.01em" }}>
                            Project Notes
                        </Typography>
                    </Stack>
                    <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 500 }}>
                        {notesList.length} {notesList.length === 1 ? "entry" : "entries"}
                    </Typography>
                </Box>
            )}

            {/* Notes List Area */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: isCompact ? 1.5 : 2.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: isCompact ? 2 : 3,
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                        bgcolor: "neutral.outlinedBorder",
                        borderRadius: "10px",
                    },
                }}
            >
                {!loading && notesList.length === 0 && (
                    <Typography level="body-xs" sx={{ textAlign: "center", mt: 4, color: "text.tertiary" }}>
                        No notes recorded.
                    </Typography>
                )}

                {loading && (
                    <Typography level="body-xs" sx={{ textAlign: "center", mt: 4, color: "text.tertiary" }}>
                        Loading activity...
                    </Typography>
                )}

                {notesList.map((note) => {
                    const isAuthor = (user as any)?.user_id === note.CreatedBy || (user as any)?.Id === note.CreatedBy;
                    const isEditing = editingNoteId === note.Id;

                    return (
                        <Box
                            key={note.Id}
                            sx={{
                                display: "flex",
                                gap: isCompact ? 1.5 : 2,
                                position: "relative",
                                "&:hover .note-actions": { opacity: 1 },
                                // Timeline line (thread)
                                "&:not(:last-child)::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: isCompact ? "12px" : "18px",
                                    top: isCompact ? "28px" : "40px",
                                    bottom: isCompact ? "-16px" : "-24px",
                                    width: "1.5px",
                                    bgcolor: "divider",
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <Avatar
                                variant="soft"
                                color={isAuthor ? "primary" : "neutral"}
                                sx={{
                                    width: isCompact ? 24 : 36,
                                    height: isCompact ? 24 : 36,
                                    fontSize: isCompact ? "10px" : "13px",
                                    mt: 0.25,
                                    boxShadow: "sm",
                                    border: "2px solid",
                                    borderColor: "background.surface",
                                }}
                            >
                                {getInitials(note.CreatedByName)}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: isCompact ? 0.3 : 0.6,
                                    }}
                                >
                                    <Typography
                                        level="title-sm"
                                        sx={{
                                            fontSize: isCompact ? "12px" : "14px",
                                            fontWeight: 700,
                                            color: "text.primary",
                                        }}
                                    >
                                        {note.CreatedByName || "User"}
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            fontSize: isCompact ? "10px" : "11px",
                                            color: "text.tertiary",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.4,
                                            fontWeight: 500,
                                        }}
                                    >
                                        <Clock size={isCompact ? 10 : 12} />
                                        {moment(note.CreatedAt).fromNow()}
                                    </Typography>

                                    {isAuthor && !isEditing && (
                                        <Stack
                                            direction="row"
                                            className="note-actions"
                                            spacing={0.5}
                                            sx={{ ml: "auto", opacity: 0, transition: "opacity 0.2s ease-in-out" }}
                                        >
                                            <Tooltip title="Edit Note" size="sm" variant="soft">
                                                <JoyIconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    sx={{
                                                        minWidth: 24,
                                                        height: 24,
                                                        borderRadius: "md",
                                                        "&:hover": { bgcolor: "background.level2" },
                                                    }}
                                                    onClick={() => handleEditStart(note)}
                                                >
                                                    <Pencil size={13} />
                                                </JoyIconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete" size="sm" variant="soft">
                                                <JoyIconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="danger"
                                                    sx={{
                                                        minWidth: 24,
                                                        height: 24,
                                                        borderRadius: "md",
                                                        "&:hover": { bgcolor: "danger.softBg" },
                                                    }}
                                                    onClick={() => handleDelete(note.Id)}
                                                >
                                                    <Trash2 size={13} />
                                                </JoyIconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Box>

                                {isEditing ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        <JoyTextarea
                                            size="sm"
                                            value={editContent}
                                            onChange={(e) => setEditContent(e.target.value)}
                                            autoFocus
                                            sx={{
                                                mb: 1,
                                                borderRadius: "md",
                                                border: "2px solid",
                                                borderColor: "primary.300",
                                                boxShadow: "sm",
                                                "--Textarea-focusedHighlight": "none",
                                            }}
                                        />
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                                onClick={handleEditCancel}
                                                sx={{ fontSize: "12px", height: "28px" }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="solid"
                                                color="primary"
                                                onClick={() => handleEditSave(note.Id)}
                                                startDecorator={<Check size={14} />}
                                                sx={{ fontSize: "12px", height: "28px", fontWeight: 700 }}
                                            >
                                                Update
                                            </Button>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Sheet
                                        variant="outlined"
                                        sx={{
                                            p: isCompact ? 1.25 : 1.75,
                                            borderRadius: isCompact ? "0 10px 10px 10px" : "0 14px 14px 14px",
                                            bgcolor: isAuthor ? "primary.softBg" : "background.level1",
                                            borderColor: isAuthor ? "primary.100" : "divider",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                                            position: "relative",
                                            transition: "transform 0.2s, box-shadow 0.2s",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                                            },
                                        }}
                                    >
                                        <Typography
                                            level={isCompact ? "body-sm" : "body-md"}
                                            sx={{
                                                fontSize: isCompact ? "13px" : "14px",
                                                lineHeight: 1.6,
                                                color: "text.primary",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                fontWeight: 450,
                                            }}
                                        >
                                            {note.Content}
                                        </Typography>
                                    </Sheet>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Input Bar */}
            <Box
                sx={{
                    p: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 0.5,
                        pl: 1.75,
                        borderRadius: "32px",
                        bgcolor: "background.level1",
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "background-color 0.2s, border-color 0.2s, box-shadow 0.2s",
                        "&:focus-within": {
                            borderColor: "primary.300",
                            bgcolor: "background.surface",
                            boxShadow: "0 0 0 3px rgba(var(--joy-palette-primary-mainChannel) / 0.12)",
                        },
                    }}
                >
                    <JoyTextarea
                        placeholder="Write a note..."
                        variant="plain"
                        color="neutral"
                        size="md"
                        minRows={1}
                        maxRows={5}
                        sx={{
                            flex: 1,
                            "--Textarea-focusedHighlight": "none",
                            "&::after": { display: "none" },
                            "&::before": { display: "none" },
                            bgcolor: "transparent",
                            color: "text.primary",
                            fontSize: "14px",
                            boxShadow: "none !important",
                            border: "none !important",
                            "&.Mui-focused": {
                                boxShadow: "none !important",
                                outline: "none !important",
                                border: "none !important",
                            },
                        }}
                        slotProps={{
                            textarea: {
                                sx: {
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                    border: "none !important",
                                    "&:focus": {
                                        outline: "none !important",
                                        boxShadow: "none !important",
                                        border: "none !important",
                                    },
                                },
                            },
                        }}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                    />
                    <JoyIconButton
                        variant="solid"
                        color="primary"
                        size="sm"
                        disabled={!newComment.trim()}
                        onClick={handleSubmit}
                        sx={{
                            borderRadius: "50%",
                            minWidth: 36,
                            height: 36,
                        }}
                    >
                        <Send size={16} />
                    </JoyIconButton>
                </Box>
            </Box>
        </Box>
    );
}
